import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * UI preferences store.
 *
 * Persists user preferences to localStorage for cross-session persistence.
 */
export const useUIStore = create(
  persist(
    (set) => ({
      // =======================================================================
      // Layout State
      // =======================================================================
      sidebarCollapsed: false,
      facesBarCollapsed: false,

      // =======================================================================
      // Display Preferences
      // =======================================================================
      showVizImage: true,
      showCropMask: true,
      rainEnabled: true,
      showHotkeys: false,

      // =======================================================================
      // User Preferences
      // =======================================================================
      // Default action when clicking a photo in overview: 'edit' opens editor, 'view' opens preview
      defaultClickAction: 'edit',
      // Auto-enter crop mode when opening a photo in edit view
      autoEnterCropMode: false,

      // =======================================================================
      // Sorting and Filtering
      // =======================================================================
      sortOrder: 'oldest', // newest, oldest, filename_asc, filename_desc, capture_newest, capture_oldest

      // =======================================================================
      // Time Range Filter
      // =======================================================================
      timeRange: null, // { start: Date, end: Date } or null for all

      // =======================================================================
      // Actions
      // =======================================================================
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      toggleFacesBar: () => set((state) => ({ facesBarCollapsed: !state.facesBarCollapsed })),
      toggleVizImage: () => set((state) => ({ showVizImage: !state.showVizImage })),
      toggleCropMask: () => set((state) => ({ showCropMask: !state.showCropMask })),
      toggleRain: () => set((state) => ({ rainEnabled: !state.rainEnabled })),
      toggleHotkeys: () => set((state) => ({ showHotkeys: !state.showHotkeys })),

      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setFacesBarCollapsed: (collapsed) => set({ facesBarCollapsed: collapsed }),
      setSortOrder: (order) => set({ sortOrder: order }),
      setTimeRange: (range) => set({ timeRange: range }),
      setShowVizImage: (show) => set({ showVizImage: show }),
      setShowCropMask: (show) => set({ showCropMask: show }),
      setRainEnabled: (enabled) => set({ rainEnabled: enabled }),
      setDefaultClickAction: (action) => set({ defaultClickAction: action }),
      setAutoEnterCropMode: (enabled) => set({ autoEnterCropMode: enabled }),
    }),
    {
      name: 'matrix-ui-preferences',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist these fields
        sidebarCollapsed: state.sidebarCollapsed,
        facesBarCollapsed: state.facesBarCollapsed,
        showVizImage: state.showVizImage,
        showCropMask: state.showCropMask,
        rainEnabled: state.rainEnabled,
        showHotkeys: state.showHotkeys,
        sortOrder: state.sortOrder,
        defaultClickAction: state.defaultClickAction,
        autoEnterCropMode: state.autoEnterCropMode,
      }),
    }
  )
);

// Selector hooks for optimized re-renders
export const useSidebarCollapsed = () => useUIStore((state) => state.sidebarCollapsed);
export const useFacesBarCollapsed = () => useUIStore((state) => state.facesBarCollapsed);
export const useShowVizImage = () => useUIStore((state) => state.showVizImage);
export const useSortOrder = () => useUIStore((state) => state.sortOrder);
export const useRainEnabled = () => useUIStore((state) => state.rainEnabled);
export const useDefaultClickAction = () => useUIStore((state) => state.defaultClickAction);
export const useAutoEnterCropMode = () => useUIStore((state) => state.autoEnterCropMode);
